<template>
    <div class="nav-menu">
        <div class="nav-menu-sidebar" :style="{ display: !isAdaptiveMenuOpen && isAdaptive ? 'none' : 'flex' }">
            <div>
                <div class="nav-menu-sidebar__logo">
                    <RouterLink class="nav-menu-sidebar__logo-src" to="/"></RouterLink>
                    <VButton @click="closeMenu" class="button_close nav-menu-sidebar__logo-close">
                        <TimesSvg></TimesSvg>
                    </VButton>
                </div>
                <div class="nav-menu-sidebar__header">
                    <VButton class="button button_green button_fluid prevent-close" @click="showRequestCreateModal"
                        >Создать обращение</VButton
                    >
                </div>
                <div class="nav-menu-sidebar__list">
                    <ul v-if="navigationType">
                        <template v-for="(item, index) in items">
                            <li :key="index" class="nav-menu-sidebar__item" :class="item.class" @click="closeMenu">
                                <div class="nav-menu-sidebar__devider" v-if="shouldShowStoreDividerTop(item)"></div>

                                <RouterLink :to="item.to" class="nav-menu-sidebar__item-link" nav-menu>
                                    <component :is="item.icon"></component>
                                    <span class="nav-menu-sidebar__item-label">
                                        {{ item.label }}
                                        <span v-show="item.counter" class="nav-menu-sidebar__item-label-counter">{{
                                            item.counter
                                        }}</span>
                                    </span>
                                </RouterLink>

                                <div class="nav-menu-sidebar__devider" v-if="shouldShowStoreDividerBottom(item)"></div>
                            </li>
                        </template>
                    </ul>
                </div>
            </div>
            <div class="nav-menu-sidebar__user-list">
                <div class="nav-menu-sidebar__item-link-user-details" v-show="toggleDetailsStatus">
                    <UserDetails :account="$_account" v-show="toggleDetailsStatus"></UserDetails>
                </div>
                <div class="nav-menu-sidebar__item-link" @click.stop="showDetails">
                    <UserCard :account="$_account" multiline />
                </div>
            </div>
        </div>
        <!-- Пять мест всегда: «Создать» в середине, «Меню» последней, остальные три —
             навигация. Пустое место занимает свою долю ширины, но ничего не рисует:
             иначе «Создать» сдвинулась бы с середины. -->
        <nav class="nav-menu-tabbar">
            <template v-for="(item, index) in tabbarSlots">
                <RouterLink
                    class="nav-menu-tabbar__item"
                    nav-menu
                    v-if="item && item.to"
                    :to="item.to"
                    :key="'link-' + index"
                    @click.native="closeMenu"
                >
                    <component :is="item.icon"></component>
                    <span class="nav-menu-tabbar__item-label">{{ item.label }}</span>
                    <span class="nav-menu-tabbar__item-counter" v-show="item.counter">{{ item.counter }}</span>
                </RouterLink>

                <button
                    type="button"
                    class="nav-menu-tabbar__item"
                    v-else-if="item"
                    :key="'action-' + index"
                    @click.stop="item.onClick"
                >
                    <component :is="item.icon"></component>
                    <span class="nav-menu-tabbar__item-label">{{ item.label }}</span>
                    <span class="nav-menu-tabbar__item-counter" v-show="item.counter">{{ item.counter }}</span>
                </button>

                <span class="nav-menu-tabbar__item" aria-hidden="true" v-else :key="'empty-' + index"></span>
            </template>
        </nav>
    </div>
</template>
<script>
// Components
import VButton from '../../components/VButton/VButton.vue';
import UserCard from '@/views/components/VUser/UserCard.vue';
import UserDetails from '@/views/components/VUser/UserDetails.vue';
import ModalNames from '@/js/enums/ModalNames';

//SVG
import IncomingRequestsSVG from '@/images/incoming_requests.svg';
import OutcomingRequestsSVG from '@/images/outcoming_requests.svg';
import ArchiveSVG from '@/images/archive.svg';
import AllRequestsSVG from '@/images/all_requests.svg';
import HistorySVG from '@/images/history.svg';
import EquipmentSVG from '@/images/equipment.svg';
import SettingsSVG from '@/images/settings.svg';
import LogoutSVG from '@/images/logout.svg';
import DotsSVG from '@/images/dots-menu-horizontal.svg';
import PlusSVG from '@/images/plus.svg';
import TimesSvg from '@/images/times.svg';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import oidc from '../../../vue-oidc-client';
import http from '@/js/http.js';
import { arrangeTabs, countUnread, NAV_SLOTS } from '@/js/navMenuTabs';
import { canExportRequests } from '@/js/requestExport';

const Workspace = namespace('workspace');
const widthMediaQuery = window.matchMedia('(max-width: 800px)');

@Component({
    components: {
        VButton,
        UserCard,
        UserDetails,
        SettingsSVG,
        LogoutSVG,
        DotsSVG,
        PlusSVG,
        TimesSvg,
    },
})
class NavMenu extends Vue {
    @Workspace.State menuInfo;
    @Workspace.State accessSecurityGroups;
    @Workspace.State requestPermissions;

    toggleDetailsStatus = false;
    isAdaptive = widthMediaQuery.matches ? true : false;
    isAdaptiveMenuOpen = false;
    navigationType = null;

    logout() {
        oidc.signOut();
    }

    // Навигация панели — свой список, короче бокового меню и в своём порядке приоритета:
    // на три места из пяти попадают первые три пункта. У исполнителя это «Все обращения»,
    // а «Архив» уходит в полное меню; у обычного пользователя наоборот.
    get tabbarNavItems() {
        const nav = [];

        if (this.navigationType === 'my_only') {
            nav.push({
                to: { name: 'request', params: { method: 'my' } },
                icon: AllRequestsSVG,
                label: 'Мои обращения',
                counter: this.menuInfo?.my ?? 0,
            });
        } else {
            nav.push(
                {
                    to: { name: 'request', params: { method: 'inbox' } },
                    icon: IncomingRequestsSVG,
                    label: 'Входящие',
                    counter: this.menuInfo?.inbox ?? 0,
                },
                {
                    to: { name: 'request', params: { method: 'outbox' } },
                    icon: OutcomingRequestsSVG,
                    label: 'Исходящие',
                    counter: this.menuInfo?.outbox ?? 0,
                },
            );
        }

        if (this.$_isExecutor || this.$_isAdmin || canExportRequests(this.requestPermissions, 'store')) {
            nav.push({
                to: { name: 'request', params: { method: 'store' } },
                icon: AllRequestsSVG,
                label: 'Все обращения',
            });
        }

        if (this.navigationType !== 'my_only') {
            nav.push({
                to: { name: 'request', params: { method: 'archive' } },
                icon: ArchiveSVG,
                label: 'Архив',
            });
        }

        return nav;
    }

    get tabbarSlots() {
        return arrangeTabs(
            this.tabbarNavItems,
            // «Создать» — действие, а не маршрут, и на телефоне это единственная
            // кнопка создания обращения.
            { icon: PlusSVG, label: 'Создать', onClick: this.showRequestCreateModal },
            { icon: DotsSVG, label: 'Меню', onClick: this.openMenu, counter: this.menuCounter },
        );
    }

    // На бейдже «Меню» — непрочитанное всего, что в панель не попало: пунктов, которым
    // не хватило места, и групп доступа, которых в панели нет вовсе. Иначе на телефоне
    // непрочитанное по группам не видно нигде.
    get menuCounter() {
        const shown = new Set(this.tabbarNavItems.slice(0, NAV_SLOTS.length).map((item) => item.to.params.method));

        return countUnread(this.items.filter((item) => !shown.has(item.to?.params?.method)));
    }

    get items() {
        const items = [];
        if (this.navigationType === 'my_only') {
            items.push({
                to: {
                    name: 'request',
                    params: {
                        method: 'my',
                    },
                },
                icon: AllRequestsSVG,
                label: 'Мои обращения',
                counter: this.menuInfo?.my ?? 0,
            });
        } else {
            items.push(
                {
                    to: {
                        name: 'request',
                        params: { method: 'inbox' },
                    },
                    icon: IncomingRequestsSVG,
                    label: 'Входящие',
                    counter: this.menuInfo?.inbox ?? 0,
                },
                {
                    to: {
                        name: 'request',
                        params: { method: 'outbox' },
                    },
                    icon: OutcomingRequestsSVG,
                    label: 'Исходящие',
                    counter: this.menuInfo?.outbox ?? 0,
                },
                {
                    to: {
                        name: 'request',
                        params: { method: 'archive' },
                    },
                    icon: ArchiveSVG,
                    label: 'Архив',
                },
            );
        }

        if (this.$_isExecutor || this.$_isAdmin || canExportRequests(this.requestPermissions, 'store')) {
            items.push({
                to: {
                    name: 'request',
                    params: {
                        method: 'store',
                    },
                },
                icon: AllRequestsSVG,
                label: 'Все обращения',
                class: this.navigationType === 'my_only' ? '' : 'nav-menu__item_space-top',
            });
        }

        if (this.$_isExecutor) {
            items.push({
                to: { name: 'enps-dashboard' },
                icon: HistorySVG,
                label: 'Оценка качества',
            });
        }

        // Модуль «Оборудование» — не заявки, поэтому стоит после «Оценки качества», ниже
        // разделителя, отбивающего блок обращений, и выше групп доступа из AD.
        //
        // Скрытие пункта — только половина дела: маршруты модуля закрыты beforeEnter
        // в routes/Inventory.js, иначе прямая ссылка всё равно открывала бы раздел.
        if (this.$_canViewInventory) {
            items.push({
                to: { name: 'inventory.items' },
                icon: EquipmentSVG,
                label: 'Оборудование',
            });
        }

        const groups = this.accessSecurityGroups ?? {};

        if (groups) {
            let groupKeys = [];

            if (this.menuInfo?.groups) {
                groupKeys = Object.keys(this.menuInfo.groups);
            }

            groups.forEach((group, index) => {
                let counter = 0;

                if (groupKeys.find((item) => item == group.id)) {
                    counter = this.menuInfo.groups[group.id];
                }

                const item = {
                    to: {
                        name: 'request',
                        params: {
                            method: group.id,
                        },
                    },
                    icon: AllRequestsSVG,
                    label: group.name,
                    counter: counter,
                    class: index === 0 ? 'nav-menu__item_space-top' : '',
                };

                items.push(item);
            });
        }

        return items;
    }

    async showRequestCreateModal() {
        const request = await this.$showModal(ModalNames.REQUEST_CREATE);

        const method = this.navigationType === 'my_only' ? 'my' : 'outbox';

        this.$router.push({
            name: 'request',
            params: {
                method,
                id: request.id,
            },
        });
    }

    toggleDetails() {
        this.toggleDetailsStatus = !this.toggleDetailsStatus;
    }

    showDetails() {
        if (!this.toggleDetailsStatus) this.toggleDetailsStatus = true;
    }

    hideDetails() {
        if (this.toggleDetailsStatus) this.toggleDetailsStatus = false;
    }

    openMenu() {
        this.isAdaptiveMenuOpen = true;
    }

    closeMenu() {
        this.isAdaptiveMenuOpen = false;
    }

    isStoreItem(item) {
        return !!(item.to && item.to.params && item.to.params.method === 'store');
    }

    shouldShowStoreDividerTop(item) {
        // В режиме "один пункт" (my_only) "Все обращения" идут сразу под "Мои обращения", и
        // разделять их чертой не нужно. Верхний разделитель нужен только когда "Все обращения"
        // следуют за блоком Входящие/Исходящие/Архив.
        return this.isStoreItem(item) && this.navigationType !== 'my_only';
    }

    shouldShowStoreDividerBottom(item) {
        // Нижний разделитель отделяет "Все обращения" от групп исполнителей вне зависимости от
        // выбранного режима меню.
        return this.isStoreItem(item);
    }

    updateStyles() {
        this.isAdaptive = widthMediaQuery.matches ? true : false;
    }
    async loadNavigationSettings() {
        try {
            const { data } = await http.get('/account-settings');
            this.navigationType = data['request-store.navigation-type'] ?? 'inbox_outbox_archive';
        } catch (error) {
            this.navigationType = 'inbox_outbox_archive';
        }
    }
    async mounted() {
        document.addEventListener('click', this.hideDetails);
        widthMediaQuery.addEventListener('change', this.updateStyles);

        await this.loadNavigationSettings();

        this.$bus.on('settings:updated', this.loadNavigationSettings);
    }

    destroyed() {
        document.removeEventListener('click', this.hideDetails);
        widthMediaQuery.removeEventListener('change', this.updateStyles);
        this.$bus.off('settings:updated', this.loadNavigationSettings);
    }
}

export default NavMenu;
</script>
<style lang="scss">
.router-link-active[nav-menu] {
    background: #f1f6fe;
}

::-webkit-scrollbar {
    width: 7px;
    border-radius: 12px;
}
::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 12px;
}

::-webkit-scrollbar-track {
    background-color: #f1f1f1;
}
.nav-menu {
    height: 100%;
    width: 100%;
}
.nav-menu-tabbar {
    display: flex;
    position: fixed;
    align-items: stretch;
    left: 0;
    right: 0;
    bottom: 0;
    // Высота панели плюс безопасная зона устройства: на iPhone с индикатором Home нижние
    // 34 точки принадлежат системе, и кнопки, дотянутые до самого низа, перекрывались бы
    // индикатором. На устройствах без выреза env() равен нулю — высота остаётся прежней.
    // Ту же добавку резервирует под панель оболочка приложения (см. App.vue).
    height: calc(3.75rem + env(safe-area-inset-bottom, 0px));
    gap: 0.375rem;
    padding: 0.3125rem 0.375rem calc(0.3125rem + env(safe-area-inset-bottom, 0px));
    // Панель висит поверх списка: без фона сквозь неё проезжали строки.
    background-color: #fff;
    box-shadow: 0 0 0.6875rem 0 rgba(0, 0, 0, 0.09);

    &__item {
        display: flex;
        position: relative;
        // Ширину делим поровну вместо прежних `width: 20%` — панель подстраивается
        // под любое число кнопок. min-width: 0 обязателен: иначе подпись не даёт
        // кнопке сжиматься, и панель уезжает за экран.
        flex: 1 1 0;
        min-width: 0;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.125rem;
        padding: 0.25rem 0.125rem;
        border: none;
        border-radius: 10px;
        background: none;
        color: #484848;
        font: 400 0.625rem/1 var(--font-family);
        text-align: center;
        text-decoration: none;
        cursor: pointer;

        svg {
            flex-shrink: 0;
            width: 1.2rem;
            height: 1.2rem;
            color: #484848;
        }
    }

    &__item-label {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__item-counter {
        position: absolute;
        top: 0.125rem;
        left: 50%;
        min-width: 16px;
        margin-left: 0.25rem;
        padding: 0 4px;
        border-radius: 8px;
        background-color: #00a55a;
        color: #fff;
        font: 600 10px/16px var(--font-family);
    }
}

.nav-menu-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: 101;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    justify-content: space-between;
    display: flex;
    flex-direction: column;
    &__list {
        max-height: 55vh;
        margin: 0 10px;
    }

    &__header {
        margin: 0 10px 14px 10px;
        .button {
            font-size: 1rem;
            border-radius: 10px;
        }
    }

    &__item {
        width: 100%;
        height: auto;
        min-height: 40px;
        margin-bottom: 4px;
        font-size: 1rem;
        color: var(--text-black);
    }

    &__item-link {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        min-height: 40px;
        padding: 0 16px;
        border-radius: 10px;
        cursor: pointer;
        position: relative;

        & svg {
            flex-shrink: 0;
            width: 24px;
            height: 24px;
            color: #484848;
        }
        &:hover {
            background: #f8f8f8;
        }

        .user-card {
            height: auto;
            width: inherit;
            display: flex;
            justify-content: center;
            &__label {
                white-space: normal;
                text-align: center;
            }
            &__label-name {
                font-size: 1rem;
            }
        }

        &-user-details {
            position: absolute;
        }
    }

    // Подпись забирает всё место до правого края, счётчик стоит в потоке у этого края.
    // Раньше он висел поверх строки (position: absolute), и длинная подпись — в узком
    // меню или крупным текстом — уходила под него. Теперь подпись переносится до счётчика.
    &__item-label {
        flex: 1 1 auto;
        min-width: 0;
        margin-left: 8px;
        display: flex;
        align-items: center;
        gap: 8px;
        overflow-wrap: anywhere;

        &-counter {
            flex-shrink: 0;
            // -6px: отступ ссылки справа 16px, а счётчик стоял в 10px от края — место то же.
            margin-left: auto;
            margin-right: -6px;
            background-color: #00a55a;
            border-radius: 12px;
            padding: 0 7px;
            color: #ffffff;
            font-size: 0.8125rem;
        }
    }

    &__logo {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        .button_close {
            position: absolute;
            right: 2rem;
            fill: var(--modal-close-button-fill);
            width: 16px;
            height: 16px;
            padding: 10px 10px 10px 30px;
            margin: -5px;
            box-sizing: content-box;
            cursor: pointer;
            box-shadow: none;
        }
    }

    &__logo-src {
        display: block;
        width: 158px;
        height: 44px;
        background: url(~@/images/talan-logo.svg?url) center center no-repeat;
        background-size: 100%;
    }

    &__devider {
        width: auto;
        border-radius: 1px;
        display: flex;
        justify-content: center;
        margin: 6px 24px;
        border-bottom: 1px var(--border-column-color) solid;
    }

    &__user-list {
        margin: 0px 10px 10px 10px;
    }
}

@media (max-width: $media-md) {
    // Панель меню в адаптиве растянута на весь экран и клики не перехватывает
    // (см. App.vue). Возвращаем их тому, что в ней реально видно.
    .nav-menu-sidebar,
    .nav-menu-tabbar {
        pointer-events: auto;
    }
}

@media (min-width: $media-md) {
    .nav-menu-tabbar {
        display: none;
    }
    .nav-menu-sidebar {
        position: static;
        &__logo {
            .button_close {
                display: none;
            }
        }
    }
}
// Прежде здесь подписи прятались на узком экране. Правило было мёртвым (панель
// рендерила классы сайдбара), и восстанавливать его не стали: у всех групп доступа
// одна и та же иконка, и без подписи такие кнопки неразличимы. Вместо этого лишние
// кнопки уходят в «Ещё», а подпись у оставшихся обрезается многоточием.
</style>
